import React, { FC, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FormattedHTMLMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import useCounter from '@app/hooks/useCounters';
import usePermissions from '@app/hooks/usePermissions';

import { routeTo } from '@app/helpers/RoutesHelper';

import ROUTES from '@app/router/Routes';
import { logoutUser } from '@app/store/login/login.thunk';

import Icon, { IconSize } from '@app/components/Icon/Icon';

import * as builder from '../ducks/builder';

import { InterfaceMenuConfig, MenuItem } from './MenuConfig.types';

const checkItemPermissions = (
    item: MenuItem,
    hasAllPermissions: (p: string[]) => boolean,
    hasAnyPermissions: (p: string[]) => boolean,
): boolean => {
    if (!item.permissions || item.permissions.length === 0) return true;
    if (item.hasAnyPermissions) return hasAnyPermissions(item.permissions);
    return hasAllPermissions(item.permissions);
};

const isDisplayed = (item: MenuItem): boolean => (item.displayed ? item.displayed() : true);

interface RootState {
    builder: {
        menuConfig: InterfaceMenuConfig;
        layoutConfig: Record<string, unknown>;
    };
}

interface MobileMenuOverlayProps {
    isOpen: boolean;
    onClose: () => void;
}

const SubItem: FC<{ item: MenuItem; onClose: () => void }> = ({ item, onClose }) => {
    const counter = useCounter(item.counter?.id ?? '');
    const linkTo = item.page || '#';
    const params = { counter: counter > 0 ? counter : -1 };

    return (
        <li className="mobile-menu-overlay__subitem">
            <Link to={linkTo} onClick={onClose} className="mobile-menu-overlay__subitem-link">
                <span className="mobile-menu-overlay__subitem-bullet">•</span>
                <span className="mobile-menu-overlay__subitem-label">
                    {item.translate ? (
                        <FormattedHTMLMessage id={item.translate} values={params} />
                    ) : (
                        item.page
                    )}
                </span>
                {item.counter && counter > 0 && (
                    <span className="mobile-menu-overlay__badge">
                        {counter.toLocaleString('fr-FR')}
                    </span>
                )}
            </Link>
        </li>
    );
};

const MenuRootItem: FC<{ item: MenuItem; isExpanded: boolean; isActive: boolean; onToggle: () => void; onClose: () => void }> = ({
    item,
    isExpanded,
    isActive,
    onToggle,
    onClose,
}) => {
    const { hasAllPermissions, hasAnyPermissions } = usePermissions();

    const checkPermissions = (entry: MenuItem) =>
        checkItemPermissions(entry, hasAllPermissions, hasAnyPermissions);

    const renderIcon = () => {
        if (item.icon) {
            return <span className="mobile-menu-overlay__item-icon"><Icon icon={item.icon} /></span>;
        }
        if (item.svg) {
            // item.svg is a custom SVG React component — Icon only supports la-* names, so we render it directly
            const SvgIcon = item.svg;
            return <span className="mobile-menu-overlay__item-icon"><SvgIcon /></span>;
        }
        return <span className="mobile-menu-overlay__item-icon-placeholder" />;
    };

    if (item.submenu) {
        return (
            <li className="mobile-menu-overlay__item">
                <button
                    type="button"
                    className={`mobile-menu-overlay__item-btn${isExpanded || isActive ? ' mobile-menu-overlay__item-btn--active' : ''}`}
                    onClick={onToggle}
                >
                    {renderIcon()}
                    <span className="mobile-menu-overlay__item-label">
                        {item.translate && <FormattedHTMLMessage id={item.translate} />}
                    </span>
                    <Icon icon={isExpanded ? 'angle-down' : 'angle-right'} className="mobile-menu-overlay__chevron" />
                </button>
                {isExpanded && (
                    <ul className="mobile-menu-overlay__submenu">
                        {item.submenu
                            .filter(isDisplayed)
                            .filter((sub) => checkPermissions(sub))
                            .map((sub, i) => (
                                <SubItem key={sub.page || i} item={sub} onClose={onClose} />
                            ))}
                    </ul>
                )}
            </li>
        );
    }

    return (
        <li className="mobile-menu-overlay__item">
            <Link
                to={item.page || '#'}
                className={`mobile-menu-overlay__item-btn${isActive ? ' mobile-menu-overlay__item-btn--active' : ''}`}
                onClick={onClose}
            >
                {renderIcon()}
                <span className="mobile-menu-overlay__item-label">
                    {item.translate && <FormattedHTMLMessage id={item.translate} />}
                </span>
            </Link>
        </li>
    );
};

const isItemActive = (item: MenuItem, pathname: string): boolean => {
    if (item.submenu) return item.submenu.some((sub) => sub.page && pathname.startsWith(sub.page));
    return Boolean(item.page && pathname.startsWith(item.page));
};

// A separator is only rendered if it carries its own content (icon or label).
const isVisibleItem = (item: MenuItem): boolean => !item.separator || Boolean(item.svg) || Boolean(item.translate);

const getItemId = (item: MenuItem, index: number): string => item.id || item.translate || String(index);

const MobileMenuOverlay: FC<MobileMenuOverlayProps> = ({ isOpen, onClose }) => {
    const { menuConfig } = useSelector((store: RootState) => store.builder);
    const dispatch = useDispatch();
    const { hasAllPermissions, hasAnyPermissions } = usePermissions();
    const { pathname } = useLocation();
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const logo = useSelector(builder.selectors.getStickyLogo);

    const checkPermissions = (item: MenuItem) =>
        checkItemPermissions(item, hasAllPermissions, hasAnyPermissions);

    const handleToggle = (id: string) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    const visibleItems = menuConfig.aside.items
        .filter(isVisibleItem)
        .filter(isDisplayed)
        .filter(checkPermissions);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            const activeIndex = visibleItems.findIndex((item) => isItemActive(item, pathname));
            if (activeIndex !== -1) {
                setExpandedId(getItemId(visibleItems[activeIndex], activeIndex));
            }

            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape') onClose();
            };
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.body.style.overflow = '';
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
        document.body.style.overflow = '';
        setExpandedId(null);
        // Run only on open/close transitions: pathname is captured at opening time
        // (the overlay closes on navigation, so it never changes while open).
    }, [isOpen]); // eslint-disable-line

    if (!isOpen) return null;

    return (
        <div className="mobile-menu-overlay" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="mobile-menu-overlay__header">
                <Link to="/" onClick={onClose}>
                    <img
                        alt="logo"
                        src={logo}
                        className="mobile-menu-overlay__logo"
                    />
                </Link>
                <button
                    type="button"
                    className="mobile-menu-overlay__close"
                    onClick={onClose}
                    aria-label="Fermer le menu"
                >
                    <Icon icon="times" size={IconSize.TwoX} />
                </button>
            </div>

            <ul className="mobile-menu-overlay__body">
                {visibleItems.map((item, index) => {
                    const itemId = getItemId(item, index);
                    return (
                        <MenuRootItem
                            key={itemId}
                            item={item}
                            isExpanded={expandedId === itemId}
                            isActive={isItemActive(item, pathname)}
                            onToggle={() => handleToggle(itemId)}
                            onClose={onClose}
                        />
                    );
                })}
            </ul>

            <div className="mobile-menu-overlay__footer">
                <Link
                    to={routeTo(ROUTES.ACCOUNT_SETTINGS.PATH)}
                    onClick={onClose}
                    className="mobile-menu-overlay__footer-info"
                >
                    <FormattedHTMLMessage id="MENU.MY_ACCOUNT" />
                </Link>
                <Button
                    variant="outline-link"
                    className="mobile-menu-overlay__footer-logout"
                    onClick={() => dispatch(logoutUser())}
                >
                    <FormattedHTMLMessage id="TRANSLATOR.LOGOUT" />
                </Button>
            </div>
        </div>
    );
};

export default MobileMenuOverlay;
